// Organization List Page
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Search, Plus, Building2, ChevronLeft, ChevronRight } from 'lucide-react';

interface Organization {
  id: string;
  name: string;
  domain: string;
  plan: string;
  status: string;
  license_count: number;
  active_licenses: number;
  mrr: number;
  created_at: string;
  churn_risk_score: number | null;
  // 18H §4.1 — ledger column (install/platform/saas), derived server-side
  // from the org's license_class set. 'saas' wins if the org holds any.
  ledger: 'saas' | 'platform' | 'install';
}

export function OrganizationListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 20;

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      // Backend shape: { data: org[], pagination: { total } }.
      const resp = await apiRequest<any>(`/organizations?${params}`);
      const rows: Organization[] = (resp.data || []).map((o: any) => ({
        id: o.id,
        name: o.name,
        domain: o.domain || '—',
        plan: o.org_kind || 'account',
        status: o.active === false ? 'inactive' : 'active',
        // Real aggregates from the backend (licenses + license_mrr_summary view).
        license_count: Number(o.license_count) || 0,
        active_licenses: Number(o.active_licenses) || 0,
        mrr: Number(o.mrr) || 0,
        created_at: o.created_at,
        churn_risk_score: null,
        ledger: o.ledger || 'install',
      }));
      setOrgs(rows);
      setTotal(resp.pagination?.total ?? rows.length);
    } catch {
      setOrgs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Organizations</h1>
          <p className="page-subtitle">{total} organizations</p>
        </div>
        <Link to="/organizations/new">
          <button style={{ background: 'var(--accent)', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={14} /> Add Organization
          </button>
        </Link>
      </div>

      <div style={{ marginBottom: '16px', position: 'relative', maxWidth: '320px' }}>
        <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input
          type="text"
          placeholder="Search organizations..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
              {['Organization', 'Domain', 'Ledger', 'Plan', 'Licenses', 'MRR', 'Churn Risk', 'Created'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>Loading...</td></tr>
            ) : orgs.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>No organizations found</td></tr>
            ) : orgs.map(org => (
              <tr key={org.id} style={{ borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
              >
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Building2 size={14} style={{ color: 'var(--accent)' }} />
                    </div>
                    <div>
                      <Link to={`/organizations/${org.id}`} style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none' }}>
                        {org.name}
                      </Link>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-secondary)' }}>{org.domain}</td>
                <td style={{ padding: '12px 16px' }}><StatusBadge status={org.ledger} /></td>
                <td style={{ padding: '12px 16px', fontSize: '12px', textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{org.plan}</td>
                <td style={{ padding: '12px 16px', fontSize: '12px' }}>
                  <span style={{ fontWeight: 600 }}>{org.active_licenses}</span>
                  <span style={{ color: 'var(--text-muted)' }}> / {org.license_count} active</span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600 }}>
                  {org.mrr > 0 ? `$${org.mrr.toLocaleString()}` : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {org.churn_risk_score !== null ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '50px', height: '4px', background: 'var(--bg-muted)', borderRadius: '2px' }}>
                        <div style={{ height: '100%', width: `${org.churn_risk_score * 100}%`, background: org.churn_risk_score > 0.6 ? '#DC2626' : org.churn_risk_score > 0.3 ? '#D97706' : '#16A34A', borderRadius: '2px' }} />
                      </div>
                      <span style={{ fontSize: '11px', color: org.churn_risk_score > 0.6 ? '#DC2626' : org.churn_risk_score > 0.3 ? '#D97706' : '#16A34A', fontWeight: 600 }}>
                        {(org.churn_risk_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  ) : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-muted)' }}>{formatRelativeTime(org.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <span>Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button disabled={page <= 1} onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', String(page - 1)); setSearchParams(p); }} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>
                <ChevronLeft size={14} />
              </button>
              <button disabled={page >= totalPages} onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', String(page + 1)); setSearchParams(p); }} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
